import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { TextInput, Button, Title, Text, Snackbar, Switch, HelperText, useTheme, RadioButton } from 'react-native-paper';
import { createChild, getChildById, updateChild } from '../../api/childrenAPI';
import { isValidName, isValidTCID } from '../../utils/validationHelpers';
import { CustomDatePicker } from '../../components';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const AddChildScreen = ({ navigation, route }) => {
  const theme = useTheme();
  const childId = route.params?.childId;
  const isEditing = !!childId;
  
  // State variables
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    tcIdNumber: '',
    birthdate: new Date(),
    gender: '', // 'male' or 'female'
    allergies: '',
    notes: '',
    specialNeeds: false,
  });
  
  // UI state variables
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Fetch child data if editing
  useEffect(() => {
    if (isEditing) {
      fetchChildData();
    }
  }, [childId]);
  
  // Fetch child data
  const fetchChildData = async () => {
    setIsLoading(true);
    
    try {
      const response = await getChildById(childId);
      const child = response.child || response.data;
      
      if (child) {
        setFormData({
          first_name: child.first_name || '',
          last_name: child.last_name || '',
          tcIdNumber: child.tc_id_number || '',
          birthdate: child.birthdate ? new Date(child.birthdate) : new Date(),
          gender: child.gender || '',
          allergies: child.allergies || '',
          notes: child.notes || '',
          specialNeeds: child.special_needs || false,
        });
      }
    } catch (err) {
      console.error('Error fetching child data:', err);
      setSnackbarMessage(err.message || 'Çocuk bilgileri yüklenirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Input change handler
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user types
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };
  
  // Date change handler
  const handleDateChange = (selectedDate) => {
    setFormData({
      ...formData,
      birthdate: selectedDate
    });
  };
  
  // Toggle switch for special needs
  const toggleSpecialNeeds = () => {
    handleChange('specialNeeds', !formData.specialNeeds);
  };
  
  // Gender selection handler
  const handleGenderSelect = (gender) => {
    handleChange('gender', gender);
  };
  
  // Web için button renderlamak
  const renderGenderButton = (gender, icon, label) => {
    if (Platform.OS === 'web') {
      return (
        <button
          style={{
            flex: 1, 
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: formData.gender === gender ? 'rgb(125, 76, 236)' : '#FFFFFF',
            color: formData.gender === gender ? '#FFFFFF' : '#333333',
            border: '1px solid #ddd',
            borderRadius: 4,
            padding: 12,
            margin: 5,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
          }}
          onClick={() => handleGenderSelect(gender)}
          disabled={isLoading}
        >
          <span style={{marginRight: 8}}>
            {icon === 'gender-male' ? '♂' : '♀'}
          </span>
          {label}
        </button>
      );
    }
    
    return (
      <TouchableOpacity 
        style={[
          styles.genderButton,
          formData.gender === gender && {
            backgroundColor: 'rgb(125, 76, 236)',
          }
        ]}
        onPress={() => handleGenderSelect(gender)}
        disabled={isLoading}
      >
        <MaterialCommunityIcons 
          name={icon} 
          size={24} 
          color={formData.gender === gender ? '#fff' : '#757575'} 
          style={styles.genderIcon}
        />
        <Text 
          style={[
            styles.genderButtonText,
            formData.gender === gender && { color: '#fff' }
          ]}
        >
          {label}
        </Text>
      </TouchableOpacity>
    );
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // First name validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'İsim gereklidir';
    } else if (!isValidName(formData.first_name)) {
      newErrors.first_name = 'Geçerli bir isim giriniz';
    }
    
    // Last name validation
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Soyisim gereklidir';
    } else if (!isValidName(formData.last_name)) {
      newErrors.last_name = 'Geçerli bir soyisim giriniz';
    }
    
    // TC ID validation
    if (formData.tcIdNumber && !isValidTCID(formData.tcIdNumber)) {
      newErrors.tcIdNumber = 'TC Kimlik Numarası 11 rakamdan oluşmalıdır';
    }
    
    // Birthdate validation
    if (!formData.birthdate) {
      newErrors.birthdate = 'Doğum tarihi gereklidir';
    } else {
      const today = new Date();
      if (formData.birthdate > today) {
        newErrors.birthdate = 'Doğum tarihi bugünden ileri bir tarih olamaz';
      }
    }
    
    // Gender validation
    if (!formData.gender) {
      newErrors.gender = 'Cinsiyet seçmelisiniz';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Save child
  const handleSave = async () => {
    // Form validation
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    // Backend için veri uyarlama
    const childData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      tc_id_number: formData.tcIdNumber,
      birthdate: formData.birthdate.toISOString().split('T')[0], // YYYY-MM-DD formatı
      gender: formData.gender,
      allergies: formData.allergies,
      notes: formData.notes,
      special_needs: formData.specialNeeds
    };
    
    try {
      if (isEditing) {
        // Update existing child
        await updateChild(childId, childData);
        setSnackbarMessage('Çocuk bilgileri başarıyla güncellendi');
      } else {
        // Create new child
        await createChild(childData);
        setSnackbarMessage('Çocuk başarıyla eklendi');
        
        // Reset form after successful creation
        setFormData({
          first_name: '',
          last_name: '',
          tcIdNumber: '',
          birthdate: new Date(),
          gender: '',
          allergies: '',
          notes: '',
          specialNeeds: false,
        });
      }
      
      setSnackbarVisible(true);
      
      // Navigate back after a delay
      setTimeout(() => {
        navigation.goBack();
      }, 1500);
    } catch (err) {
      console.error('Error saving child:', err);
      setSnackbarMessage(err.message || 'Çocuk kaydedilirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  };
  
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
      >
        <Title style={styles.title}>
          {isEditing ? 'Çocuk Bilgilerini Düzenle' : 'Yeni Çocuk Ekle'}
        </Title>
        
        <View style={styles.formContainer}>
          {/* İsim */}
          <TextInput
            label="İsim"
            value={formData.first_name}
            onChangeText={(text) => handleChange('first_name', text)}
            style={styles.input}
            mode="outlined"
            error={!!errors.first_name}
            disabled={isLoading}
          />
          {errors.first_name && (
            <HelperText type="error">{errors.first_name}</HelperText>
          )}
          
          {/* Soyisim */}
          <TextInput
            label="Soyisim"
            value={formData.last_name}
            onChangeText={(text) => handleChange('last_name', text)}
            style={styles.input}
            mode="outlined"
            error={!!errors.last_name}
            disabled={isLoading}
          />
          {errors.last_name && (
            <HelperText type="error">{errors.last_name}</HelperText>
          )}
          
          {/* TC Kimlik Numarası */}
          <TextInput
            label="TC Kimlik Numarası (İsteğe Bağlı)"
            value={formData.tcIdNumber}
            onChangeText={(text) => handleChange('tcIdNumber', text)}
            style={styles.input}
            mode="outlined"
            keyboardType="number-pad"
            error={!!errors.tcIdNumber}
            disabled={isLoading}
            placeholder="TC Kimlik numarası girmek zorunlu değildir"
          />
          {errors.tcIdNumber && (
            <HelperText type="error">{errors.tcIdNumber}</HelperText>
          )}
          
          {/* Doğum Tarihi */}
          <CustomDatePicker
            value={formData.birthdate}
            mode="date"
            display="default"
            onChange={handleDateChange}
            maximumDate={new Date()}
            error={!!errors.birthdate}
            helperText={errors.birthdate}
            label="Doğum Tarihi"
            disabled={isLoading}
          />
          
          {/* Cinsiyet */}
          <Text style={styles.sectionTitle}>Cinsiyet <Text style={styles.requiredStar}>*</Text></Text>
          <View style={styles.genderContainer}>
            {renderGenderButton('male', 'gender-male', 'Erkek')}
            {renderGenderButton('female', 'gender-female', 'Kız')}
          </View>
          {errors.gender && (
            <HelperText type="error">{errors.gender}</HelperText>
          )}
          
          {/* Alerjiler */}
          <TextInput
            label="Alerjiler (İsteğe Bağlı)"
            value={formData.allergies}
            onChangeText={(text) => handleChange('allergies', text)}
            style={styles.input}
            mode="outlined"
            multiline
            numberOfLines={2}
            disabled={isLoading}
          />
          
          {/* Özel İhtiyaçlar */}
          <View style={styles.switchContainer}>
            <Text>Özel İhtiyaçları Var</Text>
            <Switch
              value={formData.specialNeeds}
              onValueChange={toggleSpecialNeeds}
              disabled={isLoading}
              color="rgb(125, 76, 236)"
            />
          </View>
          
          {/* Notlar */}
          <TextInput
            label="Notlar (İsteğe Bağlı)"
            value={formData.notes}
            onChangeText={(text) => handleChange('notes', text)}
            style={styles.input}
            mode="outlined"
            multiline
            numberOfLines={4}
            disabled={isLoading}
          />
          
          {/* Kaydet Düğmesi */}
          <Button
            mode="contained"
            onPress={handleSave}
            style={[styles.saveButton, {backgroundColor: 'rgb(125, 76, 236)'}]}
            loading={isLoading}
            disabled={isLoading}
            color="#fff"
          >
            {isEditing ? 'Güncelle' : 'Kaydet'}
          </Button>
        </View>
      </ScrollView>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={2000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
      >
        {snackbarMessage}
      </Snackbar>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)', 
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  title: {
    fontSize: 24,
    marginBottom: 24,
    textAlign: 'center',
    color: '#fff',
    fontWeight: 'bold',
  },
  formContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    elevation: 2,
    shadowColor: 'rgb(0, 0, 0)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 24,
  },
  input: {
    marginBottom: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 8,
    color: 'rgb(125, 76, 236)',
  },
  requiredStar: {
    color: 'rgb(210, 50, 80)',
  },
  genderContainer: {
    flexDirection: 'row',
    marginBottom: 16,
    
  },
  genderButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    flexDirection: 'row',
    backgroundColor: 'white',
  },
  genderButtonText: {
    fontSize: 16,
    color: 'rgb(90, 50, 180)',
  },
  genderIcon: {
    marginRight: 8,
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    marginBottom: 16,
  },
  saveButton: {
    marginTop: 16,
    paddingVertical: 8,
  },
  snackbar: {
    bottom: 16,
  },
});

export default AddChildScreen;
